import * as Api from '@ViewModels';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useUserSession } from '../../models/hooks/appStateHooks';

const BASE_QUERY_KEY = 'note/byPhoneCall';
const PAGE_SIZE = 25;

export function usePhoneCallNotesInfiniteQuery({ phoneCallId }: { phoneCallId: string }) {
	const userSession = useUserSession();

	return useInfiniteQuery({
		enabled: !!phoneCallId,
		queryFn: ({ pageParam }) =>
			userSession.webServiceHelper.callAsync<Api.IPagedCollection<Api.INote>>(
				Api.ImpersonationBroker.composeApiUrl({
					queryParams: { pageSize: PAGE_SIZE, pageToken: pageParam, sort: 'asc' },
					urlPath: `${BASE_QUERY_KEY}/${phoneCallId}`,
				}),
				'GET'
			),
		getNextPageParam: lastPage => lastPage.pageToken || undefined,
		initialPageParam: '',
		queryKey: [BASE_QUERY_KEY, phoneCallId],
		meta: {
			errorLogging: { fn: 'usePhoneCallNotesInfiniteQuery' },
			toastErrorMessage: 'Unable to load comments',
		},
	});
}

export function useCreatePhoneCallNoteMutation({ onSuccess }: { onSuccess?: () => void } = {}) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ phoneCallId, content }: { phoneCallId: string; content: string }) => {
			const body = new FormData();
			body.append(
				'value',
				JSON.stringify({
					content: { document: `<lev-content>${content}</lev-content>`, documentVersion: 1 },
					isForCoaching: true,
					isForOutcome: false,
					referencedEntities: {},
				})
			);
			return userSession.webServiceHelper.callAsync<Api.INote>(
				Api.ImpersonationBroker.composeApiUrl({ urlPath: `phonecall/${phoneCallId}/Note` }),
				'POST',
				body
			);
		},
		onError: (error: Api.IOperationResultNoValue) => toast.error(error?.systemMessage ?? 'Unable to post comment'),
		onSuccess: async (_, { phoneCallId }) => {
			await queryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY, phoneCallId] });
			onSuccess?.();
		},
	});
}
